import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, Server, Globe, MapPin, Network, Clock, Lock,
} from "lucide-react";
import api from "lib/api";
import ResultsMap from "components/ResultsMap";
import { flagEmoji } from "lib/country-centroids";

const PORT_CHIP =
  "inline-flex items-center rounded-md border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-xs font-mono font-medium text-blue-300";

// Tabela DNS (igual à tela de domínio): badges de tipo e flags.
const DNS_TYPE_COLORS = {
  A: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  AAAA: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  CNAME: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  MX: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  NS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  TXT: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  SOA: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  PTR: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};
const DNS_FLAG_COLORS = {
  datacenter: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  cloud: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  saas: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  dc: "bg-red-500/15 text-red-300 border-red-500/30",
  gc: "bg-red-500/15 text-red-300 border-red-500/30",
};
const isIpValue = (v) => typeof v === "string" && (/^\d{1,3}(\.\d{1,3}){3}$/.test(v) || v.includes(":"));

// capFirst: se o texto começa com letra minúscula, deixa a 1ª maiúscula
// (usado só no card "Rede e Geolocalização"). Mantém null/vazio.
function capFirst(s) {
  if (typeof s !== "string" || !s) return s;
  const c = s[0];
  return c === c.toLowerCase() && c !== c.toUpperCase() ? c.toUpperCase() + s.slice(1) : s;
}

// searchTo: URL da busca com uma dork específica.
const searchTo = (q) => `/search?q=${encodeURIComponent(q)}`;
// dork: monta "campo:valor", citando o valor quando há espaço.
const dork = (field, val) => `${field}:${/\s/.test(String(val)) ? `"${val}"` : val}`;
// normFqdn: lowercase, sem ponto final (convenção p/ a página de domínio).
const normFqdn = (s) => String(s || "").trim().toLowerCase().replace(/\.$/, "");

// SLink: valor clicável que leva a uma busca pela dork `q`.
function SLink({ q, className, children }) {
  return <Link to={searchTo(q)} className={"text-primary hover:underline " + (className || "")}>{children}</Link>;
}

function DnsTypeBadge({ t }) {
  const cls = DNS_TYPE_COLORS[t] || "bg-muted/40 text-muted-foreground border-border";
  return <span className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`}>{t || "—"}</span>;
}
// flag DNS clicável: leva à busca pela dork da categoria (cloud/saas/datacenter).
// dc/gc não têm dork direta -> chip estático.
const DNS_FLAG_FIELD = { cloud: "cloud_product", saas: "saas_product", datacenter: "datacenter" };
function DnsFlagChip({ f }) {
  const cls = DNS_FLAG_COLORS[f.kind] || "bg-muted/40 text-muted-foreground border-border";
  const base = `inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`;
  const field = DNS_FLAG_FIELD[f.kind];
  if (field) {
    return <Link to={searchTo(dork(field, f.label))} className={base + " hover:brightness-125"}>{f.label}</Link>;
  }
  return <span className={base}>{f.label}</span>;
}

// Painel com título (caixa-alta, ícone) — estilo "Summary"/"Network".
function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-4 w-4" />}
        {title}
      </div>
      {children}
    </div>
  );
}

// Linha rótulo (à esquerda, esmaecido) + valor.
function Row({ label, children }) {
  // sem valor -> não exibe a linha.
  if (children === null || children === undefined || children === false || children === "") {
    return null;
  }
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 py-1 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "border-b-2 px-4 py-2 text-sm font-medium transition-colors " +
        (active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {children}
    </button>
  );
}

function ServiceCard({ s }) {
  const [view, setView] = useState("details");
  const proto = (s.protocol || "tcp").toUpperCase();
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 p-3">
        <div className="flex flex-wrap items-center gap-2">
          {s.service
            ? <SLink q={dork("service", s.service)} className="text-sm font-semibold uppercase tracking-wide">{s.service}</SLink>
            : <span className="text-sm font-semibold uppercase tracking-wide">—</span>}
          <Link to={searchTo(`port:${s.port}`)} className="font-mono text-sm text-muted-foreground hover:text-foreground hover:underline">{s.port} / {proto}</Link>
          {s.product && (
            <Link to={searchTo(dork("product", s.product))}
              className="rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground">
              {s.product}{s.version ? ` ${s.version}` : ""}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          {s.observed_at && (
            <span className="hidden font-mono text-xs text-muted-foreground sm:inline">{s.observed_at}</span>
          )}
          <div className="flex overflow-hidden rounded-md border border-border text-xs">
            <button
              onClick={() => setView("details")}
              className={"px-2 py-1 " + (view === "details" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              Detalhes
            </button>
            <button
              onClick={() => setView("json")}
              className={"px-2 py-1 " + (view === "json" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            >
              JSON
            </button>
          </div>
        </div>
      </div>

      <div className="p-3">
        {view === "json" ? (
          <pre className="max-h-96 overflow-auto rounded bg-background p-3 text-xs text-muted-foreground">
            {JSON.stringify(s, null, 2)}
          </pre>
        ) : (
          <div className="space-y-3 text-sm">
            {(s.product || s.version || s.extrainfo) && (
              <Row label="Produto">
                {[s.product, s.version].filter(Boolean).join(" ")}
                {s.extrainfo && <span className="text-muted-foreground"> · {s.extrainfo}</span>}
              </Row>
            )}
            {s.cpe && <Row label="CPE"><SLink q={dork("cpe", s.cpe)} className="font-mono text-xs">{s.cpe}</SLink></Row>}
            {s.ostype && <Row label="OS"><SLink q={dork("ostype", s.ostype)}>{s.ostype}</SLink></Row>}
            {s.banner && (
              <div>
                <div className="mb-1 text-muted-foreground">Banner</div>
                <pre className="overflow-x-auto rounded bg-background p-2 text-xs leading-relaxed text-foreground/80">{s.banner}</pre>
              </div>
            )}
            {Array.isArray(s.scripts) && s.scripts.length > 0 && (
              <div>
                <div className="mb-1 text-muted-foreground">Scripts (NSE)</div>
                <div className="space-y-2">
                  {s.scripts.map((sc, i) => (
                    <div key={i} className="rounded border border-border/60 bg-background/50 p-2">
                      <div className="font-mono text-xs font-semibold text-primary">{sc.id}</div>
                      {sc.output && (
                        <pre className="mt-1 overflow-x-auto text-xs text-muted-foreground">{sc.output}</pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!s.banner && !s.product && !(s.scripts?.length) && (
              <div className="text-sm text-muted-foreground">Sem detalhes adicionais.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CertBlock({ c, portService }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <Link to={`/certificate/${c.fingerprint}`} className="flex items-center gap-2 font-medium text-primary hover:underline">
          <Lock className="h-4 w-4 text-amber-400/80" />
          {c.subject_cn || c.fingerprint}
        </Link>
        <div className="flex flex-wrap justify-end gap-1.5">
          {(c.ports || []).map((p) => (
            <span key={p} className={PORT_CHIP}>{p}/{(portService && portService[p]) || "tcp"}</span>
          ))}
        </div>
      </div>
      <div className="space-y-2 font-mono text-xs leading-relaxed">
        <div>
          <div className="text-muted-foreground">Issued By:</div>
          {c.issuer_cn && <div className="pl-4">|- Common Name: <SLink q={dork("issuer", c.issuer_cn)}>{c.issuer_cn}</SLink></div>}
          {c.issuer_org && <div className="pl-4">|- Organization: <SLink q={dork("issuer", c.issuer_org)}>{c.issuer_org}</SLink></div>}
        </div>
        <div>
          <div className="text-muted-foreground">Issued To:</div>
          {c.subject_cn && <div className="pl-4">|- Common Name: <span className="text-foreground">{c.subject_cn}</span></div>}
          {c.subject_org && <div className="pl-4">|- Organization: <span className="text-foreground">{c.subject_org}</span></div>}
        </div>
        {(c.san_dns?.length > 0 || c.san_ip?.length > 0) && (
          <div>
            <div className="text-muted-foreground">Alternate Names:</div>
            {c.san_dns?.length > 0 && (
              <div className="pl-4">|- DNS: {c.san_dns.map((d, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ", "}
                  <Link to={searchTo(normFqdn(d))} className="text-primary hover:underline">{d}</Link>
                </React.Fragment>
              ))}</div>
            )}
            {c.san_ip?.length > 0 && (
              <div className="pl-4">|- IP: {c.san_ip.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ", "}
                  <Link to={`/host/${p}`} className="text-primary hover:underline">{p}</Link>
                </React.Fragment>
              ))}</div>
            )}
          </div>
        )}
        {(c.not_before || c.not_after) && (
          <div className="text-muted-foreground">Validade: {c.not_before || "?"} → {c.not_after || "?"}</div>
        )}
        <div className="break-all text-muted-foreground/70">{c.fingerprint}</div>
      </div>
    </div>
  );
}

export default function HostDetail() {
  const { ip } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("services");

  useEffect(() => {
    api
      .get(`/host/${ip}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Falha ao carregar host."));
  }, [ip]);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  const m = data.meta || {};
  const services = data.services || [];
  // mapa porta -> serviço (p/ os chips de porta dos certificados, estilo Resumo).
  const portService = {};
  services.forEach((s) => { if (s.port != null) portService[s.port] = s.service || s.protocol || "tcp"; });
  const certs = data.certs || [];
  const dns = data.dns || [];
  const httpResponses = data.http_responses || [];
  const httpVariantCount = httpResponses.reduce((n, r) => n + (r.variants?.length || 0), 0);
  const loc = [m.city, m.region, m.country].filter(Boolean).join(", ");
  const mapCities =
    Number.isFinite(m.lat) && Number.isFinite(m.lon)
      ? [{ city: m.city, region: m.region, country: m.country, lat: m.lat, lon: m.lon, count: 1 }]
      : [];

  return (
    <div className="w-full space-y-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" /> Resultados
      </button>

      {/* Cabeçalho do host */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg border border-border bg-background p-2.5">
              <Server className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-2xl font-semibold">{data.ip}</h1>
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Host</span>
              </div>
              {loc && (
                <div className="mt-1 text-sm text-muted-foreground">
                  {flagEmoji(m.country)} {loc}
                </div>
              )}
            </div>
          </div>
          {(m.last_seen || m.first_seen) && (
            <div className="text-right text-xs text-muted-foreground">
              {m.last_seen && (
                <>
                  <div className="flex items-center justify-end gap-1">
                    <Clock className="h-3.5 w-3.5" /> Última vez
                  </div>
                  <div className="font-mono">{m.last_seen}</div>
                </>
              )}
              {m.first_seen && (
                <div className="mt-1 font-mono opacity-70">1ª vez: {m.first_seen}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resumo + Rede/Geolocalização */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Resumo" icon={Server}>
          <Row label="Reverse DNS">
            {m.reverse_dns
              ? <Link to={searchTo(normFqdn(m.reverse_dns))} className="text-primary hover:underline">{m.reverse_dns}</Link>
              : null}
          </Row>
          <Row label="Forward DNS">
            {m.forward_dns?.length ? (
              <span className="break-words">
                {m.forward_dns.map((f, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ", "}
                    <Link to={searchTo(normFqdn(f))} className="text-primary hover:underline">{f}</Link>
                  </React.Fragment>
                ))}
              </span>
            ) : null}
          </Row>
          <Row label="OS">{m.os ? <SLink q={dork("ostype", m.os)}>{m.os}</SLink> : null}</Row>
          <Row label={`${services.length} Serviços`}>
            {services.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {services.map((s, i) => (
                  <Link key={i} to={searchTo(`port:${s.port}`)} className={PORT_CHIP + " hover:brightness-125"}>
                    {s.port}/{(s.service || s.protocol || "tcp")}
                  </Link>
                ))}
              </div>
            ) : null}
          </Row>
        </Panel>

        <Panel title="Rede e Geolocalização" icon={Network}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Row label="ASN">
                {m.asn != null ? <SLink q={`asn:${m.asn}`}>{`AS${m.asn}`}</SLink> : null}
              </Row>
              <Row label="Organização">
                {m.asn_org
                  ? (m.asn != null
                      ? <SLink q={`asn:${m.asn}`}>{capFirst(m.asn_org)}</SLink>
                      : capFirst(m.asn_org))
                  : null}
              </Row>
              <Row label="País">
                {m.country
                  ? <SLink q={dork("country", m.country)}>{`${flagEmoji(m.country)} ${m.country}`}</SLink>
                  : null}
              </Row>
              <Row label="Cloud">
                {m.cloud_product
                  ? <SLink q={dork(m.cloud ? "cloud_provider" : "cloud_product", m.cloud_product)}>{capFirst(m.cloud_product)}</SLink>
                  : null}
              </Row>
              <Row label="SaaS">
                {m.saas_product
                  ? <SLink q={dork("saas_product", m.saas_product)}>{capFirst(m.saas_product)}</SLink>
                  : null}
              </Row>
              <Row label="Datacenter">
                {m.datacenter
                  ? <SLink q={m.cloud ? dork("cloud_region", m.cloud.region) : dork("datacenter", m.datacenter)}>{capFirst(m.datacenter)}</SLink>
                  : null}
              </Row>
              {m.cloud && (
                <>
                  <Row label="Cloud região">
                    {m.cloud.region ? <SLink q={dork("cloud_region", m.cloud.region)}>{m.cloud.region}</SLink> : null}
                  </Row>
                  <Row label="Cloud serviço">
                    {m.cloud.service ? <SLink q={dork("cloud_service", m.cloud.service)}>{capFirst(m.cloud.service)}</SLink> : null}
                  </Row>
                </>
              )}
            </div>
            <div>
              {mapCities.length > 0 ? (
                <>
                  <ResultsMap cities={mapCities} />
                  <div className="mt-2 flex items-center gap-1 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                    {loc || "—"}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {m.lat?.toFixed(4)}°, {m.lon?.toFixed(4)}°
                  </div>
                </>
              ) : (
                <div className="flex h-full items-center gap-2 text-xs text-muted-foreground">
                  <Globe className="h-4 w-4" /> sem dados de geolocalização
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* Abas */}
      <div className="flex gap-1 border-b border-border">
        <TabButton active={tab === "services"} onClick={() => setTab("services")}>
          Serviços ({services.length})
        </TabButton>
        <TabButton active={tab === "certs"} onClick={() => setTab("certs")}>
          Certificados ({certs.length})
        </TabButton>
        <TabButton active={tab === "dns"} onClick={() => setTab("dns")}>
          DNS ({dns.length})
        </TabButton>
        {httpVariantCount > 0 && (
          <TabButton active={tab === "http"} onClick={() => setTab("http")}>
            HTTP/SNI ({httpVariantCount})
          </TabButton>
        )}
      </div>

      {tab === "services" && (
        <div className="space-y-3">
          {services.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              Nenhum serviço.
            </div>
          )}
          {services.map((s, i) => <ServiceCard key={i} s={s} />)}
        </div>
      )}

      {tab === "certs" && (
        <div className="space-y-3">
          {certs.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              Nenhum certificado.
            </div>
          )}
          {certs.map((c, i) => <CertBlock key={i} c={c} portService={portService} />)}
        </div>
      )}

      {tab === "dns" && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {dns.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Nenhum registro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Hostname</th>
                    <th className="px-4 py-2 font-medium">Tipo</th>
                    <th className="px-4 py-2 font-medium">Valor</th>
                    <th className="px-4 py-2 font-medium">Flags</th>
                    <th className="px-4 py-2 font-medium">Última vez</th>
                  </tr>
                </thead>
                <tbody>
                  {dns.map((r, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.fqdn
                          ? <Link to={searchTo(normFqdn(r.fqdn))} className="text-primary hover:underline">{r.fqdn}</Link>
                          : "—"}
                      </td>
                      <td className="px-4 py-2"><DnsTypeBadge t={r.record_type} /></td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {isIpValue(r.value) ? (
                          <Link to={`/host/${r.value}`} className="text-primary hover:underline">{r.value}</Link>
                        ) : (
                          <span className="break-all">{r.value || "—"}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {r.flags?.length ? (
                          <div className="flex flex-wrap gap-1">{r.flags.map((f, j) => <DnsFlagChip key={j} f={f} />)}</div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                        {r.observed_at ? String(r.observed_at).slice(0, 10) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "http" && (
        <div className="space-y-3">
          {httpResponses.map((r, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Globe className="h-4 w-4 text-primary" /> Porta {r.port}
              </div>
              {r.default && <HttpResp label="Sem SNI (padrão)" resp={r.default} muted />}
              {r.variants.map((v, j) => (
                <HttpResp key={j} label={v.sni} resp={v} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// HttpResp renderiza uma resposta HTTP (título + banner) rotulada pelo SNI.
function HttpResp({ label, resp, muted }) {
  return (
    <div className="mb-3 last:mb-0 rounded border border-border/60 bg-background/50 p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className={"font-mono text-xs " + (muted ? "text-muted-foreground" : "text-primary")}>{label}</span>
        {resp.title && <span className="truncate text-sm font-medium">{resp.title}</span>}
      </div>
      {resp.banner && (
        <pre className="overflow-x-auto rounded bg-background p-2 text-xs leading-relaxed text-foreground/80">{resp.banner}</pre>
      )}
    </div>
  );
}
